import React from "react";
import {
  ArrowRight,
  CalendarDays,
  BarChart3,
  Palette,
  CheckCircle2,
} from "lucide-react";
import "../styles/LandingPage.css";
import { gymConfig } from "../config/gymConfig";

type DemoBrand = {
  key: string;
  name: string;
  initials: string;
  domain: string;
  badge: string;
  previewStyle: "performance" | "industrial" | "wellness" | "boutique";
  heroEyebrow: string;
  heroTitle: string;
  heroAccent: string;
  heroDescription: string;
  heroPoints: string[];
  summary: string;
  stripCopy: string;
  stripEmphasis: string;
  statCards: Array<{
    label: string;
    value: string;
  }>;
  memberTracking: Array<{
    label: string;
    value: string;
  }>;
  schedule: Array<{
    time: string;
    className: string;
    bookings: string;
  }>;
  highlights: string[];
  theme: {
    bg: string;
    bgSoft: string;
    card: string;
    cardStrong: string;
    border: string;
    text: string;
    textSoft: string;
    accent: string;
    accentStrong: string;
    orb1: string;
    orb2: string;
    accentRgb: string;
    accentStrongRgb: string;
    tone: "light" | "dark";
    buttonText: string;
    displayFont: string;
    bodyFont: string;
    labelFont: string;
  };
};

const demoBrands: DemoBrand[] = [
  {
    key: "locker-room",
    name: "Locker Room",
    initials: "LR",
    domain: "demo.uselockerroom.com",
    badge: "Original platform identity",
    previewStyle: "performance",
    heroEyebrow: "Bespoke software for functional gyms",
    heroTitle: "Run your gym like a",
    heroAccent: "premium product",
    heroDescription:
      "Locker Room gives functional gyms a custom-branded platform for scheduling, bookings, member engagement, performance tracking, and PT delivery.",
    heroPoints: [
      "Custom branded per gym",
      "Separate deployment per client",
      "Built for modern functional gyms",
    ],
    summary:
      "Bold blue accents, dark surfaces, and a premium performance-led feel for modern functional gyms.",
    stripCopy: "Not another generic gym app. Not another clunky booking tool.",
    stripEmphasis: "Locker Room is bespoke software, built per gym.",
    statCards: [
      { label: "Today’s bookings", value: "84" },
      { label: "Attendance rate", value: "91%" },
      { label: "Current streak leader", value: "17" },
    ],
    memberTracking: [
      { label: "Back squat", value: "145kg" },
      { label: "5K pace", value: "4:28/km" },
      { label: "Food diary", value: "6-day streak" },
      { label: "Classes attended", value: "18 this month" },
    ],
    schedule: [
      { time: "06:00", className: "Hyrox Engine", bookings: "18/20 booked" },
      { time: "09:30", className: "Strength", bookings: "14/16 booked" },
      { time: "18:00", className: "Conditioning", bookings: "22/24 booked" },
    ],
    highlights: [
      "Custom colours, logo, and member-facing branding",
      "Class booking and admin controls",
      "Attendance insights and engagement features",
      "Optional PT programming and training logs",
      "Hosted at gymname.uselockerroom.com",
    ],
    theme: {
      bg: "#090b10",
      bgSoft: "#0f131c",
      card: "rgba(17, 22, 31, 0.78)",
      cardStrong: "rgba(20, 26, 37, 0.96)",
      border: "rgba(121, 144, 255, 0.16)",
      text: "#ffffff",
      textSoft: "#b3bccb",
      accent: "#4f6ef7",
      accentStrong: "#7b90ff",
      orb1: "rgba(79, 124, 255, 0.18)",
      orb2: "rgba(110, 169, 255, 0.12)",
      accentRgb: "79, 110, 247",
      accentStrongRgb: "123, 144, 255",
      tone: "dark",
      buttonText: "#ffffff",
      displayFont: "\"Avenir Next\", \"Segoe UI\", sans-serif",
      bodyFont: "\"Inter\", \"Segoe UI\", sans-serif",
      labelFont: "\"IBM Plex Sans\", \"Segoe UI\", sans-serif",
    },
  },
  {
    key: "north-crossfit",
    name: "North CrossFit",
    initials: "NF",
    domain: "northcrossfit.uselockerroom.com",
    badge: "CrossFit gym brand",
    previewStyle: "industrial",
    heroEyebrow: "CrossFit software for competitive communities",
    heroTitle: "Built for boxes that train",
    heroAccent: "hard, together, every day",
    heroDescription:
      "North CrossFit turns the member experience into something tougher, clearer, and more CrossFit-native, from WOD bookings to attendance, leaderboards, and open gym sessions.",
    heroPoints: [
      "WOD-first class scheduling",
      "Whiteboard culture and leaderboard energy",
      "A sharper operator dashboard for coaches",
    ],
    summary:
      "Steel grey, ember orange, and a harder CrossFit-box aesthetic aimed at gyms with a strong training culture.",
    stripCopy: "This one feels less boutique SaaS and more like a digital extension of a serious CrossFit box.",
    stripEmphasis: "North CrossFit proves the platform can take on a much tougher, community-driven identity.",
    statCards: [
      { label: "WOD bookings", value: "63" },
      { label: "Attendance consistency", value: "88%" },
      { label: "Top training streak", value: "21" },
    ],
    memberTracking: [
      { label: "Clean & jerk", value: "92kg" },
      { label: "2K row", value: "7:11" },
      { label: "Murph prep", value: "Week 4" },
      { label: "Classes attended", value: "22 this month" },
    ],
    schedule: [
      { time: "05:45", className: "CrossFit WOD", bookings: "16/18 booked" },
      { time: "12:00", className: "Engine Piece", bookings: "11/14 booked" },
      { time: "19:15", className: "Open Gym", bookings: "27 check-ins" },
    ],
    highlights: [
      "High-contrast orange actions and industrial dark neutrals",
      "Branded WOD, open gym, and class booking flows",
      "Leaderboard-focused engagement for committed members",
      "Coach notes and performance logs for CrossFit athletes",
      "Separate domain and launch assets for your gym",
    ],
    theme: {
      bg: "#111111",
      bgSoft: "#181818",
      card: "rgba(28, 24, 19, 0.82)",
      cardStrong: "rgba(34, 28, 22, 0.96)",
      border: "rgba(255, 147, 82, 0.18)",
      text: "#f8f4ee",
      textSoft: "#cbbba8",
      accent: "#ff7a1a",
      accentStrong: "#ffb36f",
      orb1: "rgba(255, 122, 26, 0.18)",
      orb2: "rgba(255, 191, 105, 0.12)",
      accentRgb: "255, 122, 26",
      accentStrongRgb: "255, 179, 111",
      tone: "dark",
      buttonText: "#1a120b",
      displayFont: "\"Arial Black\", \"Arial Bold\", sans-serif",
      bodyFont: "\"Trebuchet MS\", \"Segoe UI\", sans-serif",
      labelFont: "\"Courier New\", monospace",
    },
  },
  {
    key: "corehause-pilates",
    name: "COREHAUSE Pilates",
    initials: "CP",
    domain: "corehausepilates.uselockerroom.com",
    badge: "Boutique pilates studio brand",
    previewStyle: "wellness",
    heroEyebrow: "Lifestyle-led member software",
    heroTitle: "A gym platform that feels",
    heroAccent: "lighter, calmer, and premium",
    heroDescription:
      "COREHAUSE Pilates shows how the same Locker Room platform can become a soft, elevated member experience for pilates programming, studio scheduling, and higher-end retention.",
    heroPoints: [
      "Light-mode client branding",
      "Reformer and mat experiences together",
      "Retention-led design for premium members",
    ],
    summary:
      "A brighter light-mode palette for a premium pilates studio that wants the member experience to feel calm, elevated, and highly branded.",
    stripCopy: "Same platform foundation, completely different mood.",
    stripEmphasis: "This version leans pilates, hospitality, and premium retention rather than hard performance.",
    statCards: [
      { label: "Studios booked today", value: "97" },
      { label: "Reformer utilisation", value: "94%" },
      { label: "Weekly visit streak", value: "12" },
    ],
    memberTracking: [
      { label: "Reformer sessions", value: "9 this month" },
      { label: "Core consistency", value: "4-week streak" },
      { label: "Food diary", value: "Mindful logging on" },
      { label: "Classes attended", value: "14 this month" },
    ],
    schedule: [
      { time: "07:00", className: "Reformer Flow", bookings: "20/22 booked" },
      { time: "10:30", className: "Core Sculpt", bookings: "13/16 booked" },
      { time: "17:45", className: "Pilates Burn", bookings: "24/26 booked" },
    ],
    highlights: [
      "Softer premium palette with pilates-led branding",
      "Studio timetable and reformer class scheduling in one member app",
      "Member streaks and attendance visibility for retention",
      "Private session upsells and progress tracking for premium members",
      "A bespoke environment that still runs on Locker Room",
    ],
    theme: {
      bg: "#f3f8f7",
      bgSoft: "#ffffff",
      card: "rgba(255, 255, 255, 0.82)",
      cardStrong: "rgba(255, 255, 255, 0.94)",
      border: "rgba(54, 129, 128, 0.18)",
      text: "#163230",
      textSoft: "#557776",
      accent: "#2c9c98",
      accentStrong: "#7dc8c5",
      orb1: "rgba(44, 156, 152, 0.16)",
      orb2: "rgba(125, 200, 197, 0.14)",
      accentRgb: "44, 156, 152",
      accentStrongRgb: "125, 200, 197",
      tone: "light",
      buttonText: "#ffffff",
      displayFont: "Georgia, \"Times New Roman\", serif",
      bodyFont: "\"Gill Sans\", \"Helvetica Neue\", sans-serif",
      labelFont: "\"Avenir Next\", \"Segoe UI\", sans-serif",
    },
  },
  {
    key: "her-system",
    name: "Her System",
    initials: "HS",
    domain: "hersystem.uselockerroom.com",
    badge: "Women’s-only gym brand",
    previewStyle: "boutique",
    heroEyebrow: "Women’s-only member experience",
    heroTitle: "Make your gym feel like a",
    heroAccent: "strong women’s training space",
    heroDescription:
      "Her System gives the platform a more editorial, women-first personality without losing the operational depth underneath.",
    heroPoints: [
      "More considered women-first branding",
      "Premium class demand and waitlist visibility",
      "Member progress that still feels personal and high-end",
    ],
    summary:
      "A dramatic burgundy-and-rose system for a women’s-only gym that wants the member experience to feel strong, premium, and highly considered.",
    stripCopy: "It should feel like your own product, not a templated software resale.",
    stripEmphasis: "Her System turns the platform into a much more editorial women-first brand.",
    statCards: [
      { label: "Prime-time bookings", value: "71" },
      { label: "Waitlist conversion", value: "89%" },
      { label: "Member streak leader", value: "19" },
    ],
    memberTracking: [
      { label: "Strength progress", value: "+12% this cycle" },
      { label: "Run club pace", value: "5:02/km" },
      { label: "Food diary", value: "5-day streak" },
      { label: "Classes attended", value: "16 this month" },
    ],
    schedule: [
      { time: "06:30", className: "Women’s Strength", bookings: "15/18 booked" },
      { time: "13:00", className: "Express Sweat", bookings: "12/14 booked" },
      { time: "18:30", className: "Lift Club", bookings: "20/20 booked" },
    ],
    highlights: [
      "Luxury-feeling colour system with a stronger women-first identity",
      "Premium class booking flows with waitlist visibility",
      "Member-facing progress tracking and streak mechanics",
      "Branded messaging that feels personal, safe, and like your own product",
      "A fully tailored rollout while keeping the same platform core",
    ],
    theme: {
      bg: "#170d13",
      bgSoft: "#211019",
      card: "rgba(42, 18, 30, 0.8)",
      cardStrong: "rgba(50, 22, 35, 0.95)",
      border: "rgba(231, 109, 140, 0.18)",
      text: "#fff5f7",
      textSoft: "#d8b4bf",
      accent: "#c7476f",
      accentStrong: "#f08aa6",
      orb1: "rgba(199, 71, 111, 0.18)",
      orb2: "rgba(240, 138, 166, 0.12)",
      accentRgb: "199, 71, 111",
      accentStrongRgb: "240, 138, 166",
      tone: "dark",
      buttonText: "#fff5f7",
      displayFont: "\"Didot\", \"Bodoni MT\", serif",
      bodyFont: "\"Helvetica Neue\", Arial, sans-serif",
      labelFont: "\"Optima\", \"Segoe UI\", sans-serif",
    },
  },
];

export default function LandingPage() {
  const [activeDemoKey, setActiveDemoKey] = React.useState(demoBrands[0].key);
  const activeDemo =
    demoBrands.find((demo) => demo.key === activeDemoKey) ?? demoBrands[0];
  const isHerSystemDemo = activeDemo.key === "her-system";

  const pageThemeStyle = {
    "--lr-bg": activeDemo.theme.bg,
    "--lr-bg-soft": activeDemo.theme.bgSoft,
    "--lr-card": activeDemo.theme.card,
    "--lr-card-strong": activeDemo.theme.cardStrong,
    "--lr-border": activeDemo.theme.border,
    "--lr-text": activeDemo.theme.text,
    "--lr-text-soft": activeDemo.theme.textSoft,
    "--lr-accent": activeDemo.theme.accent,
    "--lr-accent-2": activeDemo.theme.accentStrong,
    "--lr-orb-1": activeDemo.theme.orb1,
    "--lr-orb-2": activeDemo.theme.orb2,
    "--lr-accent-rgb": activeDemo.theme.accentRgb,
    "--lr-accent-strong-rgb": activeDemo.theme.accentStrongRgb,
    "--lr-button-text": activeDemo.theme.buttonText,
    "--lr-font-display": activeDemo.theme.displayFont,
    "--lr-font-body": activeDemo.theme.bodyFont,
    "--lr-font-label": activeDemo.theme.labelFont,
  } as React.CSSProperties;

  const platformCards = [
    {
      key: "member-app",
      icon: CalendarDays,
      title: "Branded member app",
      description:
        "Bookings, schedules, training, workout display, and progress tracking in a product that feels like your gym.",
    },
    {
      key: "operator-tools",
      icon: BarChart3,
      title: "Coach and operator tools",
      description:
        "Attendance, admin workflows, retention visibility, and day-to-day controls built around class-based gyms.",
    },
    {
      key: "bespoke-build",
      icon: Palette,
      title: "Bespoke build per gym",
      description:
        "Your own branding, feature mix, and deployment, shaped around how your gym actually runs.",
    },
  ];

  const comparisonPoints = [
    {
      title: "Not a generic fitness app",
      description:
        "Locker Room is scoped and branded for your gym instead of forcing you into the same experience thousands of other gyms use.",
    },
    {
      title: "Built for functional gyms",
      description:
        "It fits class schedules, coach-led communities, performance culture, and studios that care about member experience.",
    },
    {
      title: "Shaped with you, not sold as-is",
      description:
        "We work through branding, rollout, and feature priorities with you, then refine the build around your feedback.",
    },
  ];

  const demoBodyByStyle = {
    performance:
      "Switch between demo brands to see how the same Locker Room core can be reframed for different gyms, audiences, and positioning.",
    industrial:
      "Switch between demo brands to see how the same Locker Room core can be reframed for tougher training culture and more operational clarity.",
    wellness:
      "Switch between demo brands to see how the same Locker Room core can be reframed for calmer branding, smoother scheduling, and premium retention.",
    boutique:
      "Switch between demo brands to see how the same Locker Room core can be reframed for more editorial branding, premium demand, and stronger presentation.",
  }[activeDemo.previewStyle];

  const ctaBodyByStyle = {
    performance:
      "Book a demo and we’ll show you the right version of Locker Room for your gym, your members, and your rollout.",
    industrial:
      "Book a demo and we’ll shape a version of Locker Room that feels right for your coaches, floor, and training culture.",
    wellness:
      "Book a demo and we’ll shape a softer, more premium version of Locker Room for your studio and members.",
    boutique:
      "Book a demo and we’ll shape a more considered, premium version of Locker Room for your brand and audience.",
  }[activeDemo.previewStyle];

  const renderMemberPreview = () => {
    switch (activeDemo.previewStyle) {
      case "industrial":
        return (
          <div className="lr-preview-layout lr-preview-layout-industrial lr-preview-layout-member">
            <div className="lr-preview-industrial-hero">
              {activeDemo.statCards.slice(0, 2).map((stat) => (
                <div className="lr-preview-industrial-stat" key={`${activeDemo.key}-${stat.label}`}>
                  <span>{stat.label}</span>
                  <strong>{stat.value}</strong>
                </div>
              ))}
            </div>
            <div className="lr-preview-industrial-board">
              <p className="lr-preview-label">Today’s classes</p>
              {activeDemo.schedule.map((session) => (
                <div className="lr-preview-schedule-row" key={`${activeDemo.key}-${session.time}`}>
                  <span>{session.time}</span>
                  <span>{session.className}</span>
                  <span>{session.bookings}</span>
                </div>
              ))}
            </div>
            <div className="lr-preview-stat lr-preview-stat-wide">
              <span>{activeDemo.statCards[2]?.label}</span>
              <strong>{activeDemo.statCards[2]?.value}</strong>
            </div>
          </div>
        );
      case "wellness":
        return (
          <div className="lr-preview-layout lr-preview-layout-wellness lr-preview-layout-member">
            <div className="lr-preview-wellness-lead">
              <div className="lr-preview-wellness-metric">
                <span>{activeDemo.statCards[0]?.label}</span>
                <strong>{activeDemo.statCards[0]?.value}</strong>
              </div>
              <div className="lr-preview-wellness-stack">
                {activeDemo.statCards.slice(1).map((stat) => (
                  <div className="lr-preview-stat" key={`${activeDemo.key}-${stat.label}`}>
                    <span>{stat.label}</span>
                    <strong>{stat.value}</strong>
                  </div>
                ))}
              </div>
            </div>
            <div className="lr-preview-wellness-flow">
              <p className="lr-preview-label">Today’s flow</p>
              {activeDemo.schedule.map((session) => (
                <div className="lr-preview-schedule-row" key={`${activeDemo.key}-${session.time}`}>
                  <span>{session.time}</span>
                  <span>{session.className}</span>
                  <span>{session.bookings}</span>
                </div>
              ))}
            </div>
          </div>
        );
      case "boutique":
        return (
          <div className="lr-preview-layout lr-preview-layout-boutique lr-preview-layout-member">
            <div className="lr-preview-boutique-banner">
              <span className="lr-preview-label">Member concierge</span>
              <strong>{activeDemo.statCards[0]?.value}</strong>
              <p>{activeDemo.statCards[0]?.label}</p>
            </div>
            <div className="lr-preview-boutique-grid">
              {activeDemo.statCards.slice(1).map((stat) => (
                <div className="lr-preview-stat" key={`${activeDemo.key}-${stat.label}`}>
                  <span>{stat.label}</span>
                  <strong>{stat.value}</strong>
                </div>
              ))}
            </div>
            <div className="lr-preview-boutique-list">
              {activeDemo.schedule.map((session) => (
                <div className="lr-preview-boutique-row" key={`${activeDemo.key}-${session.time}`}>
                  <div>
                    <span>{session.time}</span>
                    <strong>{session.className}</strong>
                  </div>
                  <em>{session.bookings}</em>
                </div>
              ))}
            </div>
          </div>
        );
      default:
        return (
          <div className="lr-preview-layout lr-preview-layout-performance lr-preview-layout-member">
            <div className="lr-preview-grid">
              {activeDemo.statCards.map((stat) => (
                <div className="lr-preview-stat" key={`${activeDemo.key}-${stat.label}`}>
                  <span>{stat.label}</span>
                  <strong>{stat.value}</strong>
                </div>
              ))}
            </div>
            <div className="lr-preview-schedule">
              {activeDemo.schedule.map((session) => (
                <div className="lr-preview-schedule-row" key={`${activeDemo.key}-${session.time}`}>
                  <span>{session.time}</span>
                  <span>{session.className}</span>
                  <span>{session.bookings}</span>
                </div>
              ))}
            </div>
          </div>
        );
    }
  };

  const renderOperatorPreview = () => (
    <div className="lr-operator-layout">
      <div className="lr-operator-desktop">
        <div className="lr-operator-topline">
          <div className="lr-operator-kpi">
            <span>Live utilisation</span>
            <strong>{activeDemo.statCards[0]?.value}</strong>
          </div>
          <div className="lr-operator-kpi">
            <span>Retention signal</span>
            <strong>{activeDemo.statCards[1]?.value}</strong>
          </div>
          <div className="lr-operator-kpi">
            <span>Priority follow-up</span>
            <strong>{activeDemo.statCards[2]?.value}</strong>
          </div>
        </div>

        <div className={`lr-operator-columns${isHerSystemDemo ? " is-editorial" : ""}`}>
          <div className="lr-operator-panel">
            <p className="lr-preview-label">Coach schedule</p>
            <div className="lr-preview-schedule">
              {activeDemo.schedule.map((session) => (
                <div className="lr-preview-schedule-row" key={`${activeDemo.key}-ops-${session.time}`}>
                  <span>{session.time}</span>
                  <span>{session.className}</span>
                  <span>{session.bookings}</span>
                </div>
              ))}
            </div>
          </div>

          <div className={`lr-operator-stack${isHerSystemDemo ? " is-editorial" : ""}`}>
            <div className="lr-operator-panel">
              <p className="lr-preview-label">Staff actions</p>
              <div className="lr-operator-task">
                <strong>Roster review</strong>
                <span>
                  {isHerSystemDemo
                    ? "Review peak-session demand and coach cover."
                    : "Check booking pressure on the busiest session and coach allocation."}
                </span>
              </div>
              <div className="lr-operator-task">
                <strong>Member retention</strong>
                <span>
                  {isHerSystemDemo
                    ? "Flag low-frequency members and trigger re-engagement."
                    : "Flag low-frequency members and trigger a branded re-engagement flow."}
                </span>
              </div>
            </div>

            <div className="lr-operator-panel">
              <p className="lr-preview-label">Brand controls</p>
              <div className="lr-operator-tag-row">
                <span className="lr-operator-tag">{isHerSystemDemo ? "Theme live" : "Theme active"}</span>
                <span className="lr-operator-tag">Content tuned</span>
                <span className="lr-operator-tag">
                  {isHerSystemDemo ? "Own deployment" : "Deployment isolated"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="lr-operator-mobile">
        <div className="lr-operator-mobile-overview">
          <p className="lr-preview-label">Admin capabilities</p>
          <h4>Everything your team needs to run the gym without the cramped mock dashboard.</h4>
        </div>

        <div className="lr-operator-mobile-capability-grid">
          <article className="lr-operator-mobile-capability-card">
            <p className="lr-preview-label">Live ops</p>
            <strong>Track the day in real time</strong>
            <ul>
              <li>{activeDemo.statCards[0]?.label}</li>
              <li>{activeDemo.schedule[0]?.className} and peak-session demand</li>
            </ul>
          </article>

          <article className="lr-operator-mobile-capability-card">
            <p className="lr-preview-label">Member retention</p>
            <strong>Spot who needs coach follow-up</strong>
            <ul>
              <li>{activeDemo.statCards[1]?.label}</li>
              <li>{activeDemo.statCards[2]?.label}</li>
            </ul>
          </article>

          <article className="lr-operator-mobile-capability-card">
            <p className="lr-preview-label">Schedule control</p>
            <strong>Review classes and staffing pressure</strong>
            <ul>
              {activeDemo.schedule.slice(0, 2).map((session) => (
                <li key={`${activeDemo.key}-capability-${session.time}`}>
                  {session.time} {session.className}
                </li>
              ))}
            </ul>
          </article>

          <article className="lr-operator-mobile-capability-card">
            <p className="lr-preview-label">Brand settings</p>
            <strong>Keep every touchpoint on-brand</strong>
            <ul>
              <li>Theme and content controls</li>
              <li>Separate deployment per gym</li>
            </ul>
          </article>
        </div>
      </div>
    </div>
  );

  const howItWorksSteps = [
    {
      title: "Scope the right version",
      description:
        "We start with your gym model, brand, member journey, and the parts of the platform you actually want live.",
    },
    {
      title: "Set the branding and modules",
      description:
        "We shape the branding, feature mix, and rollout priorities so the product fits your operation instead of a generic template.",
    },
    {
      title: "Refine with feedback",
      description:
        "We review the build together and tighten both the member-facing and coach-facing experience before launch.",
    },
    {
      title: "Launch and evolve",
      description:
        "You go live with a branded product for your gym, with room to expand the feature set as your needs grow.",
    },
  ];

  const pricingFactors = [
    "Branding and bespoke setup requirements",
    "Which modules you want from day one",
    "Member-facing tools like tracking, programming, and workout display",
    "Operator needs like bookings, admin tools, insights, and staff workflows",
  ];

  const fitChecks = [
    "Independent gyms and studios that care about brand and member experience",
    "Operators who want more than a generic booking app",
    "Teams who want a product shaped around how they actually run",
  ];

  const demoExpectations = [
    "A walkthrough of the most relevant parts of the platform for your gym",
    "A conversation about branding, rollout, and scope",
    "A clearer recommendation on timeline and pricing",
  ];

  return (
    <div
      className="lr-page"
      style={pageThemeStyle}
      data-tone={activeDemo.theme.tone}
      data-demo={activeDemo.key}
    >
      <div className="lr-bg-orb lr-bg-orb-1" />
      <div className="lr-bg-orb lr-bg-orb-2" />

      <header className="lr-nav">
        <div className="lr-brand">
          <img
            src={gymConfig.assets.logoUrl}
            alt={gymConfig.brand.name}
            className="lr-brand-logo"
          />
          <span className="lr-lockerroom-wordmark">{gymConfig.brand.name}</span>
        </div>

        <nav className="lr-nav-links">
          <a href="#features">Features</a>
          <a href="#why">
            Why <span className="lr-lockerroom-wordmark">{gymConfig.brand.name}</span>
          </a>
          <a href="#demo">Demo</a>
          <a href="#contact" className="lr-nav-cta">
            Book a demo
          </a>
        </nav>
      </header>

      <div className="lr-brand-rail" aria-label="Gym branding presets">
        <div className="lr-brand-rail-copy">
          <p className="lr-preview-label">Change the gym style</p>
          <p className="lr-brand-rail-text">
            Switch gyms to see how the same Locker Room product can be branded and positioned differently.
          </p>
        </div>
        <div className="lr-demo-switcher" role="tablist" aria-label="Gym branding presets">
          {demoBrands.map((demo) => (
            <button
              key={demo.key}
              type="button"
              className={`lr-demo-switcher-button${
                demo.key === activeDemo.key ? " is-active" : ""
              }`}
              onClick={() => setActiveDemoKey(demo.key)}
            >
              <span className="lr-demo-switcher-mark">{demo.initials}</span>
              <span>{demo.name}</span>
            </button>
          ))}
        </div>
      </div>

      <main>
        <section className="lr-hero">
          <div className="lr-hero-copy">
            <div className="lr-eyebrow">{activeDemo.heroEyebrow}</div>

            <h1>
              {activeDemo.heroTitle} <span>{activeDemo.heroAccent}</span>.
            </h1>

            <p className="lr-hero-subtext">
              Locker Room builds bespoke software for independent gyms and studios. {activeDemo.heroDescription} Built on the{" "}
              <span className="lr-lockerroom-wordmark">{gymConfig.brand.name}</span> core,
              but shaped around how your gym actually runs.
            </p>

            <div className="lr-hero-actions">
              <a href="#contact" className="lr-button lr-button-primary">
                Book a demo
                <ArrowRight size={18} />
              </a>

              <a href="#demo" className="lr-button lr-button-secondary">
                View demo
              </a>
            </div>

            <div className="lr-hero-points">
              {activeDemo.heroPoints.map((point) => (
                <div key={point}>
                  <CheckCircle2 size={16} />
                  <span>{point}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lr-hero-card-wrap">
            <div className="lr-hero-surface">
              <div className="lr-surface-header">
                <span className="lr-demo-badge">Member-facing experience</span>
                <div className="lr-preview-pill">Branded member app</div>
              </div>
              <div className="lr-dashboard-preview lr-dashboard-preview-mobile">
                <div className="lr-preview-brand">
                  <div className="lr-preview-logo" aria-hidden="true">
                    {activeDemo.initials}
                  </div>
                </div>

                <div className="lr-dashboard-top">
                  <div>
                    <p className="lr-preview-label">Demo gym</p>
                    <h3 className="lr-dashboard-domain">{activeDemo.name}</h3>
                  </div>
                  <div className="lr-preview-pill">
                    Powered by <span className="lr-lockerroom-wordmark">{gymConfig.brand.name}</span>
                  </div>
                </div>

                {renderMemberPreview()}

                <div className="lr-member-tracking">
                  <p className="lr-preview-label">Member progress</p>
                  <div className="lr-member-tracking-grid">
                    {activeDemo.memberTracking.map((item) => (
                      <div className="lr-member-tracking-item" key={`${activeDemo.key}-${item.label}`}>
                        <span>{item.label}</span>
                        <strong>{item.value}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="lr-strip">
          <p>
            Your own branded member and coach platform.
            <span> Not off-the-shelf fitness software.</span>
          </p>
        </section>

        <section className="lr-section lr-section-features" id="features" data-layout={activeDemo.previewStyle}>
          <div className="lr-section-heading">
            <p className="lr-section-kicker">What We Do</p>
            <h2>We build branded software for gyms that want a product, not just software access.</h2>
            <p>
              Locker Room combines the member experience, coach workflows, and operator tools in one bespoke platform built around your gym.
            </p>
            <div className="lr-section-brand-note">
              <strong>One platform core. Different brand directions.</strong>
              <span>{activeDemo.summary}</span>
            </div>
          </div>

          <div className="lr-feature-grid">
            {platformCards.map(({ key, icon: Icon, title, description }) => (
              <article key={key} className="lr-feature-card">
                <Icon size={22} />
                <h3>{title}</h3>
                <p>{description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="lr-section lr-section-split" id="why" data-layout={activeDemo.previewStyle}>
          <div>
            <p className="lr-section-kicker">
              Why Not Mainstream Fitness Software
            </p>
            <h2>Most fitness software is generic. Locker Room is built to fit your gym.</h2>
            <p className="lr-paragraph">
              Mainstream platforms usually make your gym adapt to their product. Locker Room starts with the product core, then shapes the branding, setup, and experience around your gym.
            </p>
          </div>

          <div className="lr-why-list">
            {comparisonPoints.map((item) => (
              <div className="lr-why-item" key={item.title}>
                <strong>{item.title}</strong>
                <span>{item.description}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="lr-section lr-section-demo" id="demo" data-layout={activeDemo.previewStyle}>
          <div className="lr-section-heading">
            <p className="lr-section-kicker">Brand Switching Demo</p>
            <h2>
              See how the same <span className="lr-lockerroom-wordmark">{gymConfig.brand.name}</span> platform
              can look completely different.
            </h2>
            <p>{demoBodyByStyle}</p>
          </div>

          <div className="lr-demo-panel">
            <div className="lr-demo-copy">
              <h3>{activeDemo.name}</h3>
              <p className="lr-demo-summary">{activeDemo.summary}</p>
              <ul>
                {activeDemo.highlights.map((highlight) => (
                  <li key={highlight}>{highlight}</li>
                ))}
              </ul>
            </div>

            <div className="lr-demo-card">
              <div className="lr-demo-card-inner">
                <div className="lr-demo-workspace">
                  <div className="lr-demo-workspace-header">
                    <span className="lr-demo-badge">Coach and operator workspace</span>
                    <div className="lr-preview-pill">
                      <span className="lr-lockerroom-wordmark">{gymConfig.brand.name}</span> platform
                    </div>
                  </div>

                  <div className="lr-dashboard-preview lr-dashboard-preview-desktop">
                    <div className="lr-dashboard-top">
                    <div>
                      <p className="lr-preview-label">Admin environment</p>
                        <h3 className="lr-dashboard-domain">{activeDemo.domain}</h3>
                      </div>
                      <div className="lr-preview-pill">Internal tools</div>
                    </div>

                    {renderOperatorPreview()}
                  </div>
                </div>

                <p className="lr-demo-card-note">
                  {activeDemo.badge}. A bespoke member platform with bookings,
                  training, and admin tools, built on the{" "}
                  <span className="lr-lockerroom-wordmark">{gymConfig.brand.name}</span>
                   platform core.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="lr-section lr-section-process" id="how-it-works">
          <div className="lr-section-heading">
            <p className="lr-section-kicker">How It Works</p>
            <h2>A straightforward process from scope to launch.</h2>
            <p>
              We don’t hand you a generic template and ask you to fit around it. We
              scope the right version of{" "}
              <span className="lr-lockerroom-wordmark">{gymConfig.brand.name}</span> with
              you, then shape the build around your gym’s needs.
            </p>
          </div>

          <div className="lr-process-grid">
            {howItWorksSteps.map((step, index) => (
              <article key={step.title} className="lr-process-step">
                <span className="lr-process-index">0{index + 1}</span>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </article>
            ))}
          </div>

          <div className="lr-pricing-panel">
            <div className="lr-pricing-copy">
              <p className="lr-section-kicker">Pricing</p>
              <h3>Pricing depends on the build, not a generic plan.</h3>
              <p>
                Every gym is different, so pricing depends on scope, rollout, and what you want included from launch.
              </p>
            </div>

            <div className="lr-pricing-list">
              {pricingFactors.map((factor) => (
                <div key={factor} className="lr-pricing-item">
                  <CheckCircle2 size={16} />
                  <span>{factor}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="lr-section lr-section-fit">
          <div className="lr-fit-grid">
            <div className="lr-fit-card">
              <p className="lr-section-kicker">Best Fit</p>
              <h3>Best suited to gyms that want something more tailored than mainstream software.</h3>
              <div className="lr-fit-list">
                {fitChecks.map((item) => (
                  <div key={item} className="lr-fit-item">
                    <CheckCircle2 size={16} />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="lr-fit-card">
              <p className="lr-section-kicker">On The Demo</p>
              <h3>You’ll leave with a much clearer picture of the right Locker Room build for your gym.</h3>
              <div className="lr-fit-list">
                {demoExpectations.map((item) => (
                  <div key={item} className="lr-fit-item">
                    <CheckCircle2 size={16} />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="lr-cta" id="contact" data-layout={activeDemo.previewStyle}>
          <div className="lr-cta-card">
            <p className="lr-section-kicker">Get started</p>
            <h2>
              Want your own <span className="lr-lockerroom-wordmark">{gymConfig.brand.name}</span> build?
            </h2>
            <p>{ctaBodyByStyle}</p>

            <div className="lr-hero-actions lr-cta-actions">
              <a href={`mailto:${gymConfig.marketing.contactEmail}`} className="lr-button lr-button-primary">
                Book a demo
                <ArrowRight size={18} />
              </a>
              <a href={`mailto:${gymConfig.marketing.contactEmail}`} className="lr-button lr-button-secondary">
                Email us
              </a>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
